import dayjs from "dayjs";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { colorSystem } from "../../theme";
import { useTheme } from "@mui/styles";
import { NutritionEntryDto } from "@growth-machine-llc/stridist-api-client";
import {
  NameType,
  ValueType,
  // eslint-disable-next-line import/no-unresolved
} from "recharts/types/component/DefaultTooltipContent";
import { Box, Card, Divider, Typography } from "@mui/material";
import { InternalNutritionEntryDto } from "../../utils/chart";
import { thousandSeparator } from "./utils/thousandSeparator";

const ChartTooltip =
  (averagingWindow) =>
  ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length) {
      return (
        <Card sx={{ padding: 1.2 }}>
          <Typography variant="subtitle1">
            {averagingWindow && averagingWindow !== "day"
              ? `${dayjs(label).startOf(averagingWindow).format("MMM D")} - ${dayjs(label).endOf(averagingWindow).format("MMM D, YYYY")}`
              : dayjs(label).format("MMMM DD, YYYY")}
          </Typography>
          {payload[0].payload.targetCalories ? (
            <>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="subtitle2" lineHeight={1.4}>
                  Target:
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={colorSystem.black}
                  lineHeight={1.4}
                >
                  {thousandSeparator(payload[0].payload.targetCalories)} kcal
                </Typography>
              </Box>
              <Divider sx={{ my: 0.7 }} />
            </>
          ) : null}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="subtitle2" lineHeight={1.4}>
              Proteins:
            </Typography>
            <Typography
              variant="subtitle2"
              color={colorSystem.nutritionProteins}
              lineHeight={1.4}
            >
              {thousandSeparator(payload[0].payload.protein || 0)} g
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="subtitle2" lineHeight={1.4}>
              Carbs:
            </Typography>
            <Typography
              variant="subtitle2"
              color={colorSystem.nutritionCarbs}
              lineHeight={1.4}
            >
              {thousandSeparator(payload[0].payload.carbs || 0)} g
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="subtitle2" lineHeight={1.4}>
              Fats:
            </Typography>
            <Typography
              variant="subtitle2"
              color={colorSystem.nutritionFats}
              lineHeight={1.4}
            >
              {thousandSeparator(payload[0].payload.fat || 0)} g
            </Typography>
          </Box>
          <Divider sx={{ my: 0.7 }} />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="subtitle2" lineHeight={1.4}>
              Calories:
            </Typography>
            <Typography
              variant="subtitle2"
              color={colorSystem.nutritionCalories}
              lineHeight={1.4}
            >
              {thousandSeparator(payload[0].payload.calories || 0)} kcal
            </Typography>
          </Box>
        </Card>
      );
    }

    return null;
  };

interface ClientNutritionCardBarChartProps {
  data: InternalNutritionEntryDto[];
  showWeekday?: boolean;
  averagingWindow?: string;
}

export default function ClientNutritionCardBarChart(
  props: ClientNutritionCardBarChartProps,
) {
  const { data, showWeekday, averagingWindow } = props;

  const theme = useTheme();

  const processedData = data
    ? data.map((entry) => ({
        ...entry,
        proteinCalories: entry.protein && entry.protein * 4,
        carbsCalories: entry.carbs && entry.carbs * 4,
        fatCalories: entry.fat && entry.fat * 9,
      }))
    : [];

  const maxValue = Math.max(
    ...processedData.map(
      (entry) =>
        (entry.proteinCalories || 0) +
        (entry.carbsCalories || 0) +
        (entry.fatCalories || 0),
    ),
    ...processedData.map((entry) => entry.targetCalories || 0),
  );

  const chartMax =
    maxValue > 1000
      ? Math.ceil((maxValue * 1.2) / 1000) * 1000
      : Math.ceil(maxValue * 1.2);

  const chartMin = Math.floor(
    Math.min(...processedData.map((entry) => entry.proteinCalories || 0)) / 1.2,
  );

  const spacedData = processedData.map((entry) => {
    const spacing = entry.calories ? (chartMax - chartMin) * 0.01 : 0;
    return {
      ...entry,
      spacing,
      targetCaloriesLine:
        entry.targetCalories > 0 ? entry.targetCalories * 1.01 : null,
    };
  });

  return (
    <Box sx={{ minWidth: "100%", height: "100%" }}>
      <ResponsiveContainer
        style={{
          fontFamily: "Montserrat",
          fontWeight: "bold",
          fontSize: 12,
        }}
        height={300}
        width="100%"
      >
        <ComposedChart
          margin={{ right: 30, top: 0, bottom: 0, left: 0 }}
          maxBarSize={20}
          barGap={10}
          data={spacedData}
        >
          <CartesianGrid
            strokeWidth={1.5}
            vertical={false}
            strokeDasharray="3 3"
          />
          <XAxis
            strokeWidth={1.5}
            dataKey="date"
            minTickGap={15}
            xAxisId={0}
            tickFormatter={(value) => dayjs(value).format(showWeekday ? "dd" : "MMM D")}
            stroke={theme.palette.text.secondary}
            fill={theme.palette.text.secondary}
          />

          {showWeekday && (
            <XAxis
              strokeWidth={1.5}
              dataKey="date"
              minTickGap={15}
              xAxisId={1}
              tickMargin={-10}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => dayjs(value).format("MMM D")}
              stroke={theme.palette.text.secondary}
              fill={theme.palette.text.secondary}
            />
          )}

          <Tooltip content={ChartTooltip(averagingWindow)} cursor={false} />

          <YAxis
            domain={[chartMin, chartMax]}
            axisLine={false}
            tickLine={false}
            strokeWidth={1.5}
            yAxisId={0}
            tickFormatter={(value) => value >= 1000 ? `${Math.round(value / 1000)}k` : value.toString()}
            stroke={theme.palette.text.secondary}
            fill={theme.palette.text.secondary}
          />

          <Bar
            dataKey="fatCalories"
            fill={colorSystem.nutritionFats}
            xAxisId={0}
            stackId="a"
            radius={5}
            stroke={colorSystem.nutritionFats}
            strokeWidth={2}
            fillOpacity={0.6}
            activeBar={<Rectangle fillOpacity={0.8} />}
          />
          <Bar dataKey="spacing" stackId="a" opacity={0} />
          <Bar
            dataKey="carbsCalories"
            fill={colorSystem.nutritionCarbs}
            xAxisId={0}
            stackId="a"
            radius={5}
            stroke={colorSystem.nutritionCarbs}
            strokeWidth={2}
            fillOpacity={0.6}
            activeBar={<Rectangle fillOpacity={0.8} />}
          />
          <Bar dataKey="spacing" stackId="a" opacity={0} />
          <Bar
            dataKey="proteinCalories"
            fill={colorSystem.nutritionProteins}
            stackId="a"
            xAxisId={0}
            radius={5}
            fillOpacity={0.6}
            stroke={colorSystem.nutritionProteins}
            strokeWidth={2}
            activeBar={<Rectangle fillOpacity={0.8} />}
          />

          <Line
            dataKey="targetCaloriesLine"
            stroke={colorSystem.blackOpacity}
            strokeWidth={2}
            fillOpacity={0.6}
            type="linear"
            strokeDasharray="5 5"
            connectNulls
            dot={{ strokeDasharray: "" }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
